import { useEffect, useRef, useCallback } from 'react';
import { prepareWithSegments, layoutWithLines } from '@chenglou/pretext';

const REPEL_RADIUS = 70;
const REPEL_STRENGTH = 45;
const SPRING = 0.09;
const DAMPING = 0.72;

function readBubbleStyle(el) {
  const cs = getComputedStyle(el);
  const pl = parseFloat(cs.paddingLeft) || 0;
  const pt = parseFloat(cs.paddingTop) || 0;
  const fs = parseFloat(cs.fontSize) || 16;
  let lh = parseFloat(cs.lineHeight);
  if (!Number.isFinite(lh) || lh <= 0) lh = fs * 1.7;
  const font = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`.replace(/\s+/g, ' ').trim();
  const innerW = el.clientWidth - pl - (parseFloat(cs.paddingRight) || 0);
  const ls = parseFloat(cs.letterSpacing) || 0;
  const baselineShift = (lh - fs) / 2;
  return { pl, pt, fs, lh, font, innerW, ls, baselineShift };
}

function buildChars(text, style) {
  if (!text?.trim() || style.innerW < 20) return [];
  try {
    const prepared = prepareWithSegments(text, style.font, { whiteSpace: 'pre-wrap' });
    const { lines } = layoutWithLines(prepared, style.innerW, style.lh);
    const chars = [];
    const mc = document.createElement('canvas').getContext('2d');
    mc.font = style.font;
    for (let li = 0; li < lines.length; li++) {
      let xOff = 0;
      const lineChars = [...lines[li].text];
      for (let ci = 0; ci < lineChars.length; ci++) {
        const ch = lineChars[ci];
        const cw = mc.measureText(ch).width;
        if (ch.trim()) {
          const hx = style.pl + xOff;
          const hy = style.pt + li * style.lh + style.baselineShift;
          chars.push({ char: ch, homeX: hx, homeY: hy, x: hx, y: hy, vx: 0, vy: 0 });
        }
        xOff += cw + style.ls;
      }
    }
    return chars;
  } catch { return []; }
}

function sizeCanvas(canvas, el, dpr) {
  const w = el.clientWidth;
  const h = el.clientHeight;
  const pw = Math.floor(w * dpr);
  const ph = Math.floor(h * dpr);
  if (canvas.width !== pw || canvas.height !== ph) {
    canvas.width = pw;
    canvas.height = ph;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
  }
  return { w, h };
}

function drawFrame(canvas, el, chars, style, color, mouse, dpr) {
  const { w, h } = sizeCanvas(canvas, el, dpr);
  const ctx = canvas.getContext('2d');
  if (!ctx) return true;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, w, h);

  if (!chars.length) return false;

  const inside = mouse.inside;
  const mx = mouse.x;
  const my = mouse.y;
  let anyMoving = false;

  ctx.font = style.font;
  ctx.textBaseline = 'top';
  ctx.fillStyle = color;
  if (style.ls && typeof ctx.letterSpacing !== 'undefined') {
    ctx.letterSpacing = `${style.ls}px`;
  }

  for (const c of chars) {
    if (inside) {
      const dx = c.x - mx;
      const dy = c.y + style.fs * 0.5 - my;
      const dist = Math.sqrt(dx * dx + dy * dy) || 1;
      if (dist < REPEL_RADIUS) {
        const force = (1 - dist / REPEL_RADIUS) * REPEL_STRENGTH;
        c.vx += (dx / dist) * force * 0.16;
        c.vy += (dy / dist) * force * 0.16;
      }
    }

    c.vx += (c.homeX - c.x) * SPRING;
    c.vy += (c.homeY - c.y) * SPRING;
    c.vx *= DAMPING;
    c.vy *= DAMPING;
    c.x += c.vx;
    c.y += c.vy;

    const drift = Math.abs(c.x - c.homeX) + Math.abs(c.y - c.homeY);
    if (drift > 0.3) anyMoving = true;

    ctx.fillText(c.char, c.x, c.y);
  }

  return inside || anyMoving;
}

export default function usePretextRepel(bubbleRef, text) {
  const canvasRef = useRef(null);
  const charsRef = useRef([]);
  const mouseRef = useRef({ x: -9999, y: -9999, inside: false });
  const rafRef = useRef(null);
  const activeRef = useRef(false);
  const styleRef = useRef(null);
  const colorRef = useRef('');
  const sizeRef = useRef({ w: 0, h: 0 });

  const rebuild = useCallback(() => {
    const el = bubbleRef.current;
    if (!el || !text?.trim()) { charsRef.current = []; return; }

    const wasTransparent = el.style.color === 'transparent';
    if (wasTransparent) el.style.color = '';
    const s = readBubbleStyle(el);
    colorRef.current = getComputedStyle(el).color || 'rgba(58, 107, 71, 0.92)';
    sizeRef.current = { w: el.clientWidth, h: el.clientHeight };
    if (wasTransparent) el.style.color = 'transparent';

    styleRef.current = s;
    const prev = charsRef.current;
    const next = buildChars(text, s);
    for (let i = 0; i < next.length; i++) {
      if (prev[i] && prev[i].char === next[i].char) {
        next[i].x = prev[i].x; next[i].y = prev[i].y;
        next[i].vx = prev[i].vx; next[i].vy = prev[i].vy;
      }
    }
    charsRef.current = next;
  }, [bubbleRef, text]);

  useEffect(() => {
    rebuild();
    if (activeRef.current && charsRef.current.length > 0) {
      startLoop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rebuild]);

  const ensureCanvas = useCallback(() => {
    const el = bubbleRef.current;
    if (!el) return null;
    let canvas = canvasRef.current;
    if (canvas && !el.contains(canvas)) {
      canvas = null;
      canvasRef.current = null;
    }
    if (!canvas) {
      canvas = document.createElement('canvas');
      canvas.style.cssText = 'position:absolute;top:0;left:0;pointer-events:none;border-radius:inherit;z-index:1;';
      el.style.position = 'relative';
      el.appendChild(canvas);
      canvasRef.current = canvas;
    }
    return canvas;
  }, [bubbleRef]);

  const deactivate = useCallback((el, canvas) => {
    if (el) el.style.color = '';
    if (canvas) {
      canvas.style.pointerEvents = 'none';
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    activeRef.current = false;
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
  }, []);

  const startLoop = useCallback(() => {
    if (rafRef.current) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const loop = () => {
      const canvas = canvasRef.current;
      const el = bubbleRef.current;
      const s = styleRef.current;
      if (!canvas || !el || !s) { rafRef.current = null; activeRef.current = false; return; }
      if (!el.contains(canvas)) { deactivate(el, null); canvasRef.current = null; return; }

      const keepGoing = drawFrame(canvas, el, charsRef.current, s, colorRef.current, mouseRef.current, dpr);

      if (!keepGoing) {
        deactivate(el, canvas);
        return;
      }

      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
  }, [bubbleRef, deactivate]);

  useEffect(() => {
    const el = bubbleRef.current;
    if (!el || !text?.trim()) return;

    const onEnter = () => {
      mouseRef.current.inside = true;
      if (activeRef.current) return;
      activeRef.current = true;
      rebuild();
      if (charsRef.current.length === 0) { activeRef.current = false; return; }
      const canvas = ensureCanvas();
      if (!canvas) { activeRef.current = false; return; }

      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const { w, h } = sizeRef.current;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      canvas.style.pointerEvents = 'auto';

      drawFrame(canvas, el, charsRef.current, styleRef.current, colorRef.current, mouseRef.current, dpr);
      el.style.color = 'transparent';

      startLoop();
    };

    const onMove = (e) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      mouseRef.current.x = e.clientX - rect.left;
      mouseRef.current.y = e.clientY - rect.top;
      mouseRef.current.inside = true;
    };

    const onLeave = () => {
      mouseRef.current.inside = false;
    };

    el.addEventListener('mouseenter', onEnter);
    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
    return () => {
      el.removeEventListener('mouseenter', onEnter);
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
      deactivate(el, canvasRef.current);
    };
  }, [bubbleRef, text, rebuild, ensureCanvas, startLoop, deactivate]);

  useEffect(() => {
    const el = bubbleRef.current;
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (el && el.style.color === 'transparent') el.style.color = '';
    };
  }, [bubbleRef]);
}
